/**
 * 全站通用的提示區塊 —— 取代 antd 的 <Alert>。
 *
 * 為什麼不用 antd Alert：
 *   它的 info 是 #e6f4ff 那種瀏覽器藍，跟整個後台的玫瑰色調完全不搭，
 *   一出現就像是別的系統的畫面貼進來。手機上圖示與文字的預設間距也太擠。
 *
 * 這支原本放在 components/shop/ShopHint.js（線上商店專用），
 * 但它跟商店沒有任何關係，現在移到 common 供全站使用。
 * shop/ShopHint.js 保留為 re-export，既有 import 不會壞。
 *
 * 用法：
 *   <Hint tone='warning' title='尚未綁定金流帳號' action={<Button/>}>
 *     說明文字…
 *   </Hint>
 *
 * tone: 'info'（預設，玫瑰色）| 'warning' | 'error' | 'success'
 */
import React from 'react'
import styled from 'styled-components'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled
} from '@ant-design/icons'

const TONES = {
  info: {
    bg: '#fdf7f9',
    border: '#f5dfe7',
    iconBg: '#f9e8ee',
    iconColor: '#d5728a',
    titleColor: '#8a4a5c'
  },
  warning: {
    bg: '#fffaf2',
    border: '#f3e3c6',
    iconBg: '#f9eed8',
    iconColor: '#c98a3d',
    titleColor: '#8a6430'
  },
  error: {
    bg: '#fdf4f4',
    border: '#f2d9d9',
    iconBg: '#f9e3e3',
    iconColor: '#c86e6e',
    titleColor: '#954949'
  },
  success: {
    bg: '#f4faf5',
    border: '#d9ecdb',
    iconBg: '#e3f2e5',
    iconColor: '#5aa668',
    titleColor: '#3f7a4a'
  }
}

const ICONS = {
  info: InfoCircleFilled,
  warning: ExclamationCircleFilled,
  error: CloseCircleFilled,
  success: CheckCircleFilled
}

const Box = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ $t }) => $t.bg};
  border: 1px solid ${({ $t }) => $t.border};
`

const IconWrap = styled.div`
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $t }) => $t.iconBg};
  color: ${({ $t }) => $t.iconColor};
  font-size: 14px;
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const HintTitle = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ $t }) => $t.titleColor};
  line-height: 1.6;
`

const Desc = styled.div`
  font-size: 13px;
  line-height: 1.8;
  color: rgba(0, 0, 0, 0.6);
  margin-top: ${({ $hasTitle }) => ($hasTitle ? '4px' : 0)};
`

const ActionWrap = styled.div`
  margin-top: 10px;
`

/* 關閉鈕。只有少數「一次性成功訊息」需要，預設不顯示 */
const CloseBtn = styled.button`
  flex: none;
  align-self: flex-start;
  margin: -2px -4px 0 4px;
  padding: 4px;
  background: none;
  border: none;
  line-height: 1;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.3);
  cursor: pointer;

  &:hover {
    color: rgba(0, 0, 0, 0.55);
  }
`

export default function Hint ({
  tone = 'info',
  title,
  action,
  style,
  closable = false,
  onClose,
  children
}) {
  const t = TONES[tone] || TONES.info
  const Icon = ICONS[tone] || ICONS.info

  return (
    <Box $t={t} style={style}>
      <IconWrap $t={t}>
        <Icon />
      </IconWrap>
      <Body>
        {title && <HintTitle $t={t}>{title}</HintTitle>}
        {children && <Desc $hasTitle={!!title}>{children}</Desc>}
        {action && <ActionWrap>{action}</ActionWrap>}
      </Body>
      {closable && (
        <CloseBtn type='button' aria-label='關閉' onClick={onClose}>
          ✕
        </CloseBtn>
      )}
    </Box>
  )
}
